import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PublicProductsAPI, PublicBannersAPI, getProductImageUrl, API_BASE_ORIGIN } from '../../Services/ECommerceAPI';
import { toast } from 'react-toastify';
import './ProductListing.css';

function getProductReviewAvg(p) {
    const v = p?.averageRating ?? p?.AverageRating;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
}

function getProductReviewCount(p) {
    const c = p?.reviewCount ?? p?.ReviewCount;
    const n = typeof c === 'number' ? c : parseInt(c, 10);
    return Number.isFinite(n) ? n : 0;
}

function ProductCardRating({ product }) {
    const count = getProductReviewCount(product);
    const avg = getProductReviewAvg(product);
    if (count <= 0) {
        return <div className="product-card-rating product-card-rating--empty">No reviews yet</div>;
    }
    const filled = Math.min(5, Math.max(0, Math.round(avg)));
    return (
        <div className="product-card-rating" aria-label={`${avg.toFixed(1)} out of 5, ${count} reviews`}>
            <span className="card-review-stars" aria-hidden>
                {[0, 1, 2, 3, 4].map((i) => (
                    <span key={i} className={i < filled ? 'star on' : 'star off'}>★</span>
                ))}
            </span>
            <span className="product-card-rating-value">{avg.toFixed(1)}</span>
            <span className="product-card-rating-count">({count})</span>
        </div>
    );
}

const ProductListing = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [banners, setBanners] = useState([]);
    const [activeBanner, setActiveBanner] = useState(0);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const organizationId = 1; // Default organization

    useEffect(() => {
        loadCategories();
        loadBanners();
        const categoryFromUrl = searchParams.get('category');
        const searchFromUrl = searchParams.get('search');
        if (categoryFromUrl) {
            setSelectedCategory(categoryFromUrl);
        }
        if (searchFromUrl) {
            setSearchQuery(searchFromUrl);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!banners.length) return;
        const t = window.setInterval(() => {
            setActiveBanner((i) => (i + 1) % banners.length);
        }, 6000);
        return () => window.clearInterval(t);
    }, [banners.length]);

    useEffect(() => {
        loadProducts();
    }, [page, selectedCategory, searchQuery]);

    const loadCategories = async () => {
        try {
            const data = await PublicProductsAPI.getAllCategories(organizationId);
            setCategories(data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadBanners = async () => {
        try {
            const data = await PublicBannersAPI.getBanners(organizationId);
            setBanners(Array.isArray(data) ? data : []);
            setActiveBanner(0);
        } catch (error) {
            setBanners([]);
        }
    };

    const loadProducts = async () => {
        try {
            setLoading(true);
            let data;
            
            if (searchQuery) {
                data = await PublicProductsAPI.searchProducts(searchQuery, organizationId, page, 20);
            } else if (selectedCategory) {
                const category = categories.find(c => c.slug === selectedCategory || c.categoryID === parseInt(selectedCategory));
                if (category) {
                    data = await PublicProductsAPI.getProductsByCategory(category.categoryID, organizationId);
                } else {
                    data = await PublicProductsAPI.getAllProducts(organizationId, page, 20);
                }
            } else {
                data = await PublicProductsAPI.getAllProducts(organizationId, page, 20);
            }

            setProducts(Array.isArray(data) ? data : []);
            // Calculate total pages if pagination info available
            const totalCount = data.length || 0;
            setTotalPages(Math.ceil(totalCount / 20));
        } catch (error) {
            console.error('Error loading products:', error);
            toast.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
    };

    const handleCategorySelect = (categorySlug) => {
        setSelectedCategory(categorySlug);
        setPage(1);
        navigate(`/shop?category=${categorySlug}`);
    };

    const handleProductClick = (product) => {
        const slug = product.slug || product.productID;
        navigate(`/product/${slug}`);
    };

    return (
        <div className="product-listing-container">
            <div className="product-listing-header banner-fullbleed">
                {banners.length === 0 ? (
                    <>
                        <h1>ViviKids Picks</h1>
                        <p>Bright, comfy, playful finds made for little everyday adventures.</p>
                    </>
                ) : (
                    <div className="banner-slider banner-slider-full">
                        {banners.map((b, idx) => {
                            const raw = b.imageUrl || b.ImageUrl || '';
                            const img = raw && raw.startsWith('/') ? `${API_BASE_ORIGIN}${raw}` : raw;
                            const title = b.title || b.Title || '';
                            const subtitle = b.subtitle || b.Subtitle || '';
                            const link = b.linkUrl || b.LinkUrl || '';
                            const isActive = idx === activeBanner;
                            return (
                                <button
                                    key={b.bannerID || b.BannerID || idx}
                                    type="button"
                                    className={`banner-slide ${isActive ? 'active' : ''}`}
                                    onClick={() => link && navigate(link)}
                                    style={img ? { backgroundImage: `url(${img})` } : undefined}
                                    aria-label={title || `Banner ${idx + 1}`}
                                >
                                    <div className="banner-overlay" />
                                    <div className="banner-content">
                                        {title && <h2>{title}</h2>}
                                        {subtitle && <p>{subtitle}</p>}
                                    </div>
                                </button>
                            );
                        })}
                        <div className="banner-dots">
                            {banners.map((_, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    className={i === activeBanner ? 'dot active' : 'dot'}
                                    onClick={() => setActiveBanner(i)}
                                    aria-label={`Go to banner ${i + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="product-listing-content">
                {/* Sidebar - Categories */}
                <div className="categories-sidebar">
                    <h3>Categories</h3>
                    <ul className="category-list">
                        <li 
                            className={!selectedCategory ? 'active' : ''}
                            onClick={() => {
                                setSelectedCategory(null);
                                setPage(1);
                                navigate('/shop');
                            }}
                        >
                            All Kids Picks
                        </li>
                        {categories.map(category => (
                            <li
                                key={category.categoryID}
                                className={selectedCategory === category.slug ? 'active' : ''}
                                onClick={() => handleCategorySelect(category.slug)}
                            >
                                {category.name}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Main Content */}
                <div className="products-main">
                    {/* Search Bar */}
                    <form onSubmit={handleSearch} className="search-bar">
                        <input
                            type="text"
                            placeholder="Search toys, outfits, gifts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        <button type="submit" className="search-button">Search</button>
                    </form>

                    {/* Products Grid */}
                    {loading ? (
                        <div className="loading-spinner">Loading products...</div>
                    ) : products.length === 0 ? (
                        <div className="no-products">No products found</div>
                    ) : (
                        <>
                            <div className="products-grid">
                                {products.map(product => (
                                    <div
                                        key={product.productID ?? product.ProductID}
                                        className="product-card"
                                        onClick={() => handleProductClick(product)}
                                    >
                                        <div className="product-image">
                                            {product.images && product.images.length > 0 ? (
                                                <img
                                                    src={getProductImageUrl(product.images[0].imageUrl)}
                                                    alt={product.name}
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                                                    }}
                                                />
                                            ) : (
                                                <div className="no-image">No Image</div>
                                            )}
                                            {product.stockQuantity === 0 && (
                                                <div className="out-of-stock-badge">Out of Stock</div>
                                            )}
                                            {product.compareAtPrice != null && product.compareAtPrice > product.price && (
                                                <span className="discount-badge">
                                                    {Math.round((1 - product.price / product.compareAtPrice) * 100)}% OFF
                                                </span>
                                            )}
                                        </div>
                                        <div className="product-info">
                                            <h3 className="product-name">{product.name}</h3>
                                            <p className="product-category">{product.category?.name || 'Uncategorized'}</p>
                                            <ProductCardRating product={product} />
                                            <div className="product-price">
                                                {product.compareAtPrice && product.compareAtPrice > product.price ? (
                                                    <>
                                                        <span className="old-price">${product.compareAtPrice.toFixed(2)}</span>
                                                        <span className="current-price">${product.price.toFixed(2)}</span>
                                                    </>
                                                ) : (
                                                    <span className="current-price">${product.price.toFixed(2)}</span>
                                                )}
                                            </div>
                                            {product.stockQuantity > 0 && (
                                                <button
                                                    className="add-to-cart-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Add to cart logic will be handled in product details
                                                        navigate(`/product/${product.slug || product.productID}`);
                                                    }}
                                                >
                                                    View Details
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination">
                                    <button
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        Previous
                                    </button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button
                                        disabled={page === totalPages}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductListing;
